import { useState, useEffect } from "react"
import Pagination from "../../Common/Pagination"
import { useNavigate } from "react-router-dom"
import axiosInstance from "../../../utils/axiosInstance"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import toast from "react-hot-toast"
import { API_ENDPOINTS } from "../../../constants/config"

export function DesignationManagement() {
  const navigate = useNavigate()
  const [designations, setDesignations] = useState([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [name, setName] = useState("")

  useEffect(() => {
    fetchDesignations()
  }, [])

  async function fetchDesignations() {
    setLoading(true)
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.designations)
      setDesignations(res.data.data)
    } catch {
      toast.error("Failed to load designations")
    }
    setLoading(false)
  }

  async function handleAdd() {
    if (!name.trim()) return toast.error("Name required")
    try {
      await axiosInstance.post(API_ENDPOINTS.designations, { name: name.trim() })
      setName("")
      setIsDialogOpen(false)
      fetchDesignations()
      toast.success("Added")
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to add")
    }
  }

  async function handleEdit() {
    if (!name.trim()) return toast.error("Name required")
    try {
      await axiosInstance.patch(API_ENDPOINTS.designation(editing._id), { name: name.trim() })
      setEditing(null)
      setName("")
      setIsDialogOpen(false)
      fetchDesignations()
      toast.success("Updated")
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to update")
    }
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete designation?")) return
    try {
      await axiosInstance.delete(API_ENDPOINTS.designation(id))
      fetchDesignations()
      toast.success("Deleted")
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to delete")
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Designations</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex justify-between mb-4">
          <Button variant="outline" onClick={() => navigate("/dashboard/settings/designation-mapping")}>
            Teacher/Staff Mapping
          </Button>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { setEditing(null); setName("") }}>Add Designation</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editing ? "Edit Designation" : "Add Designation"}</DialogTitle>
              </DialogHeader>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="Designation name" />
              <Button onClick={editing ? handleEdit : handleAdd}>{editing ? "Update" : "Add"}</Button>
            </DialogContent>
          </Dialog>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={2}>Loading...</TableCell></TableRow>
            ) : designations.length === 0 ? (
              <TableRow><TableCell colSpan={2}>No designations</TableCell></TableRow>
            ) : designations.slice((page - 1) * itemsPerPage, page * itemsPerPage).map(d => (
              <TableRow key={d._id}>
                <TableCell>{d.name}</TableCell>
                <TableCell>
                  <Button size="sm" variant="outline" onClick={() => { setEditing(d); setName(d.name); setIsDialogOpen(true) }}>Edit</Button>
                  <Button size="sm" variant="destructive" onClick={() => handleDelete(d._id)} className="ml-2">Delete</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Pagination
          currentPage={page}
          totalItems={designations.length}
          itemsPerPage={itemsPerPage}
          onPageChange={setPage}
          onItemsPerPageChange={(newLimit) => { setItemsPerPage(newLimit); setPage(1); }}
          itemLabel="designations"
        />
      </CardContent>
    </Card>
  )
}
